import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Map,
  Globe,
  Mountain,
  Moon,
  Square,
  Layers,
} from "lucide-react";
import { useMapUi } from "@/contexts/MapUiContext";
import { useQuery } from "@tanstack/react-query";
import { objectTypesApi, organizationsApi } from "@/services/api-v2.service";
import { MultiSelectCombobox, type MultiSelectOption } from "@/components/ui/multi-select-combobox";
import { klsSectionsApi } from "@/services/kls-api.service";
import { useModuleFlags } from "@/contexts/ModuleFlagsContext";
import { referenceLayersApi } from "@/services/reference-layers-api.service";
import { hasToken } from "@/lib/api-client-v2";
import { usePermissions } from "@/hooks/useAuthV2";
import { CALLOUTS_MAX_COUNT, CALLOUTS_MIN_ZOOM } from "@/lib/map-density-thresholds";
import { isMartinBasemapConfigured } from "@/lib/martin-basemap";
import {
  OI_NOM_SCALES,
  formatOiNomScaleLabel,
  loadOiNomPrefs,
  setOiNomScaleEnabled,
  OI_NOM_PREFS_EVENT,
} from "@/lib/oi-nomenclature-sheets";

const QUAL_ID_GEOMETRY_PRIMITIVE_TYPES = 343;

const BASE_LAYERS = [
  { id: "map-osm", label: "OpenStreetMap", icon: Map },
  { id: "map-satellite", label: "Спутник", icon: Globe },
  { id: "map-topo", label: "Топографическая", icon: Mountain },
  { id: "map-dark", label: "Тёмная", icon: Moon },
  { id: "map-blank", label: "Без подложки", icon: Square },
  ...(isMartinBasemapConfigured()
    ? ([{ id: "map-martin", label: "Martin (вектор)", icon: Layers }] as const)
    : []),
];

export default function LayerPanel() {
  const { flags } = useModuleFlags();
  const {
    setActiveTool,
    activeTool,
    cadastralLayersVisible,
    toggleCadastralLayers,
    cadastralOpacity,
    setCadastralOpacity,
    telegramIncidentsVisible,
    toggleTelegramIncidents,
    telegramOpacity,
    setTelegramOpacity,
    workItemsVisible,
    toggleWorkItemsVisible,
    workItemsOpacity,
    setWorkItemsOpacity,
    showWorkItemCreateButton,
    setShowWorkItemCreateButton,
    showBaseLayerSwitcher,
    setShowBaseLayerSwitcher,
    showMapExportButton,
    setShowMapExportButton,
    mvtFilters,
    setMvtFilters,
    mvtLayerSettings,
    setMvtLayerSettings,
    referenceLayerUi,
    setReferenceLayerEnabled,
    setReferenceLayerOpacity,
  } = useMapUi();

  const [activeLayer, setActiveLayer] = useState("map-osm");
  const [nomPrefs, setNomPrefs] = useState(() => loadOiNomPrefs());
  useEffect(() => {
    const sync = () => setNomPrefs(loadOiNomPrefs());
    window.addEventListener(OI_NOM_PREFS_EVENT, sync);
    return () => window.removeEventListener(OI_NOM_PREFS_EVENT, sync);
  }, []);
  const { data: permissions = [] } = usePermissions();
  const canViewReferenceLayers =
    permissions.includes("admin.access") || permissions.includes("reference_layers.view");
  const { data: referenceLayers = [] } = useQuery({
    queryKey: ["reference-layers", "catalog"],
    queryFn: () => referenceLayersApi.listForUser(),
    enabled: hasToken() && canViewReferenceLayers,
  });
  const { data: objectTypes = [] } = useQuery({
    queryKey: ["object-types", "layer-panel"],
    queryFn: () => objectTypesApi.list(),
  });
  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations", "layer-panel"],
    queryFn: () => organizationsApi.list(),
  });
  const { data: geometryTypes = [] } = useQuery({
    queryKey: ["kls", "geometry-primitive-types", "layer-panel"],
    queryFn: () => klsSectionsApi.list({ qual_id: QUAL_ID_GEOMETRY_PRIMITIVE_TYPES, limit: 1000 }),
  });

  const objectTypeOptions: MultiSelectOption[] = objectTypes.map((item) => ({
    value: String(item.id),
    label: item.name,
  }));
  const organizationOptions: MultiSelectOption[] = organizations.map((item) => ({
    value: String(item.id),
    label: item.name,
  }));
  const geometryTypeOptions: MultiSelectOption[] = geometryTypes.map((item) => ({
    value: item.kls_code,
    label: item.kls_names || item.kls_namef,
    fullLabel: item.kls_namef,
  }));

  const handleLayerChange = (layerId: string) => {
    setActiveLayer(layerId);
    setActiveTool(layerId);
  };

  useEffect(() => {
    const mappedLayer = activeTool && BASE_LAYERS.some((layer) => layer.id === activeTool)
      ? activeTool
      : "map-osm";
    setActiveLayer(mappedLayer);

    if (!activeTool) {
      setActiveTool("map-osm");
    }
  }, [activeTool, setActiveTool]);

  return (
    <div className="space-y-6 p-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Подложка карты
        </h3>
        <div className="mb-3 flex items-center justify-between rounded-md border p-2">
          <Label htmlFor="base-layer-switcher-toggle" className="text-xs cursor-pointer">
            Показывать свитчер подложек
          </Label>
          <Switch
            id="base-layer-switcher-toggle"
            checked={showBaseLayerSwitcher}
            onCheckedChange={setShowBaseLayerSwitcher}
          />
        </div>
        <div className="mb-3 flex items-center justify-between rounded-md border p-2">
          <Label htmlFor="map-export-button-toggle" className="text-xs cursor-pointer">
            Показывать кнопку экспорта
          </Label>
          <Switch
            id="map-export-button-toggle"
            checked={showMapExportButton}
            onCheckedChange={setShowMapExportButton}
          />
        </div>
        {flags.workItemsUiEnabled && (
          <div className="mb-3 flex items-center justify-between rounded-md border p-2">
            <Label htmlFor="work-item-create-button-toggle" className="text-xs cursor-pointer">
              Показывать кнопку создания задачи
            </Label>
            <Switch
              id="work-item-create-button-toggle"
              checked={showWorkItemCreateButton}
              onCheckedChange={setShowWorkItemCreateButton}
            />
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          {BASE_LAYERS.map((layer) => {
            const Icon = layer.icon;
            return (
              <Button
                key={layer.id}
                variant={activeLayer === layer.id ? "default" : "outline"}
                size="sm"
                className="justify-start gap-2 h-9"
                onClick={() => handleLayerChange(layer.id)}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="text-xs truncate">{layer.label}</span>
              </Button>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Дополнительные слои
        </h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="cadastral-switch" className="text-sm cursor-pointer">
                Кадастровые данные
              </Label>
              <Switch
                id="cadastral-switch"
                checked={cadastralLayersVisible}
                onCheckedChange={toggleCadastralLayers}
              />
            </div>
            {cadastralLayersVisible && (
              <div className="flex items-center gap-3 pl-1">
                <span className="text-xs text-gray-400 w-20 shrink-0">
                  Прозрачность
                </span>
                <Slider
                  min={0}
                  max={100}
                  step={5}
                  value={[Math.round(cadastralOpacity * 100)]}
                  onValueChange={([v]) => setCadastralOpacity(v / 100)}
                  className="flex-1"
                />
                <span className="text-xs text-gray-500 w-8 text-right">
                  {Math.round(cadastralOpacity * 100)}%
                </span>
              </div>
            )}
          </div>

          {flags.workItemsUiEnabled && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="telegram-switch" className="text-sm cursor-pointer">
                Telegram инциденты
              </Label>
              <Switch
                id="telegram-switch"
                checked={telegramIncidentsVisible}
                onCheckedChange={toggleTelegramIncidents}
              />
            </div>
            {telegramIncidentsVisible && (
              <div className="flex items-center gap-3 pl-1">
                <span className="text-xs text-gray-400 w-20 shrink-0">
                  Прозрачность
                </span>
                <Slider
                  min={0}
                  max={100}
                  step={5}
                  value={[Math.round(telegramOpacity * 100)]}
                  onValueChange={([v]) => setTelegramOpacity(v / 100)}
                  className="flex-1"
                />
                <span className="text-xs text-gray-500 w-8 text-right">
                  {Math.round(telegramOpacity * 100)}%
                </span>
              </div>
            )}
          </div>
          )}

          {flags.workItemsUiEnabled && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="work-items-switch" className="text-sm cursor-pointer">
                  Задачи и инциденты
                </Label>
                <Switch
                  id="work-items-switch"
                  checked={workItemsVisible}
                  onCheckedChange={toggleWorkItemsVisible}
                />
              </div>
              {workItemsVisible && (
                <div className="flex items-center gap-3 pl-1">
                  <span className="text-xs text-gray-400 w-20 shrink-0">
                    Прозрачность
                  </span>
                  <Slider
                    min={0}
                    max={100}
                    step={5}
                    value={[Math.round(workItemsOpacity * 100)]}
                    onValueChange={([v]) => setWorkItemsOpacity(v / 100)}
                    className="flex-1"
                  />
                  <span className="text-xs text-gray-500 w-8 text-right">
                    {Math.round(workItemsOpacity * 100)}%
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Номенклатура
        </h3>
        <p className="mb-2 text-xs text-muted-foreground">
          Топографические листы (L-36-Г…). Не путать с метровой «Сеткой» в тулбаре.
        </p>
        <div className="space-y-2">
          {OI_NOM_SCALES.map((scale) => {
            const id = `nom-scale-${scale}`;
            const on = Boolean(nomPrefs.enabledScales[String(scale)]);
            return (
              <div key={scale} className="flex items-center justify-between">
                <Label htmlFor={id} className="text-sm cursor-pointer">
                  {formatOiNomScaleLabel(scale)}
                </Label>
                <Switch
                  id={id}
                  checked={on}
                  onCheckedChange={(v) => {
                    setNomPrefs(setOiNomScaleEnabled(scale, Boolean(v)));
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>

      {canViewReferenceLayers && referenceLayers.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Справочные слои
          </h3>
          <div className="space-y-3">
            {referenceLayers.map((layer) => {
              const ui = referenceLayerUi[layer.id];
              const enabled = ui?.enabled ?? false;
              const opacity = ui?.opacity ?? layer.default_opacity;
              return (
                <div key={layer.id} className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-sm cursor-pointer leading-snug">
                      {layer.title}
                    </Label>
                    <Switch
                      checked={enabled}
                      onCheckedChange={(v) =>
                        setReferenceLayerEnabled(layer.id, v, layer.default_opacity)
                      }
                    />
                  </div>
                  {enabled && (
                    <div className="flex items-center gap-3 pl-1">
                      <span className="text-xs text-gray-400 w-20 shrink-0">
                        Прозрачность
                      </span>
                      <Slider
                        min={0}
                        max={100}
                        step={5}
                        value={[Math.round(opacity * 100)]}
                        onValueChange={([v]) =>
                          setReferenceLayerOpacity(layer.id, v / 100)
                        }
                        className="flex-1"
                      />
                      <span className="text-xs text-gray-500 w-8 text-right">
                        {Math.round(opacity * 100)}%
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Фильтры MVT
        </h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs text-gray-500">Типы объектов</Label>
            <MultiSelectCombobox
              options={objectTypeOptions}
              values={mvtFilters.objectTypes.map(String)}
              onValuesChange={(values: string[]) =>
                setMvtFilters({
                  ...mvtFilters,
                  objectTypes: values.map((v: string) => Number(v)).filter((n: number) => Number.isFinite(n)),
                })
              }
              placeholder="Все типы"
              searchPlaceholder="Поиск типа..."
              emptyText="Типы не найдены"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-gray-500">Организации</Label>
            <MultiSelectCombobox
              options={organizationOptions}
              values={mvtFilters.organizations.map(String)}
              onValuesChange={(values: string[]) =>
                setMvtFilters({
                  ...mvtFilters,
                  organizations: values.map((v: string) => Number(v)).filter((n: number) => Number.isFinite(n)),
                })
              }
              placeholder="Все организации"
              searchPlaceholder="Поиск организации..."
              emptyText="Организации не найдены"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-gray-500">Статусы</Label>
            <div className="flex flex-wrap gap-2">
              {[
                { id: "active", label: "Активные" },
                { id: "inactive", label: "Неактивные" },
                { id: "archived", label: "Архив" },
              ].map((status) => {
                const checked = mvtFilters.statuses.includes(status.id);
                return (
                  <Button
                    key={status.id}
                    variant={checked ? "default" : "outline"}
                    size="sm"
                    className="h-8"
                    onClick={() => {
                      const statuses = checked
                        ? mvtFilters.statuses.filter((s) => s !== status.id)
                        : [...mvtFilters.statuses, status.id];
                      setMvtFilters({ ...mvtFilters, statuses });
                    }}
                  >
                    {status.label}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-gray-500">Типы геометрических примитивов (KLS qual=343)</Label>
            <MultiSelectCombobox
              options={geometryTypeOptions}
              values={mvtFilters.geometryPrimitiveCodes}
              onValuesChange={(values: string[]) =>
                setMvtFilters({
                  ...mvtFilters,
                  geometryPrimitiveCodes: values,
                })
              }
              placeholder="Все типы примитивов"
              searchPlaceholder="Поиск типа примитива..."
              emptyText="Типы примитивов не найдены"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          MVT слои
        </h3>
        <div className="space-y-4">
          <div className="space-y-1 rounded-md border border-amber-200 bg-amber-50/60 p-3">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-sm cursor-pointer font-medium">
                Значки с подписями
              </Label>
              <Switch
                checked={Boolean(mvtLayerSettings.iconsLabelsMode)}
                onCheckedChange={(checked) =>
                  setMvtLayerSettings({
                    ...mvtLayerSettings,
                    iconsLabelsMode: checked,
                    ...(checked
                      ? {
                          symbolOnlyMode: false,
                          calloutsEnabled: false,
                        }
                      : {}),
                  })
                }
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Отдельный режим: без геометрий и без кластеров. Значок типа (KLS) + имя у
              каждого объекта; выносные линии к подписям. Размер по zoom. Остальные
              переключатели MVT ниже отключаются.
            </p>
          </div>

          <div
            className={
              mvtLayerSettings.iconsLabelsMode
                ? "space-y-4 opacity-50 pointer-events-none"
                : "space-y-4"
            }
          >
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm cursor-pointer">Только значки (KLS)</Label>
              <Switch
                checked={mvtLayerSettings.symbolOnlyMode}
                onCheckedChange={(checked) =>
                  setMvtLayerSettings({
                    ...mvtLayerSettings,
                    symbolOnlyMode: checked,
                    ...(checked ? { showPoints: true } : {}),
                  })
                }
                disabled={Boolean(mvtLayerSettings.iconsLabelsMode)}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Скрывает полигоны, линии и круги точек; оставляет иконки с масштабом по zoom.
            </p>
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm cursor-pointer">Callouts (подписи)</Label>
              <Switch
                checked={mvtLayerSettings.calloutsEnabled}
                onCheckedChange={(checked) =>
                  setMvtLayerSettings({ ...mvtLayerSettings, calloutsEnabled: checked })
                }
                disabled={Boolean(mvtLayerSettings.iconsLabelsMode)}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Имя объекта под значком + линия-вынос (до {CALLOUTS_MAX_COUNT}, zoom ≥{" "}
              {CALLOUTS_MIN_ZOOM}). Не на оранжевых кластерах — сначала приблизьте, пока
              разойдутся точки. Режим рисования выключите.
            </p>
          </div>

          <div
            className={
              mvtLayerSettings.symbolOnlyMode || mvtLayerSettings.iconsLabelsMode
                ? "space-y-2 opacity-50 pointer-events-none"
                : "space-y-2"
            }
          >
            <div className="flex items-center justify-between">
              <Label className="text-sm cursor-pointer">Полигоны</Label>
              <Switch
                checked={mvtLayerSettings.showPolygons}
                onCheckedChange={(checked) =>
                  setMvtLayerSettings({ ...mvtLayerSettings, showPolygons: checked })
                }
              />
            </div>
            {mvtLayerSettings.showPolygons &&
              !mvtLayerSettings.symbolOnlyMode &&
              !mvtLayerSettings.iconsLabelsMode && (
              <div className="flex items-center gap-3 pl-1">
                <span className="text-xs text-gray-400 w-20 shrink-0">Прозрачность</span>
                <Slider
                  min={0}
                  max={100}
                  step={5}
                  value={[Math.round(mvtLayerSettings.polygonOpacity * 100)]}
                  onValueChange={([v]) =>
                    setMvtLayerSettings({ ...mvtLayerSettings, polygonOpacity: v / 100 })
                  }
                  className="flex-1"
                />
                <span className="text-xs text-gray-500 w-8 text-right">
                  {Math.round(mvtLayerSettings.polygonOpacity * 100)}%
                </span>
              </div>
            )}
          </div>

          <div
            className={
              mvtLayerSettings.symbolOnlyMode || mvtLayerSettings.iconsLabelsMode
                ? "space-y-2 opacity-50 pointer-events-none"
                : "space-y-2"
            }
          >
            <div className="flex items-center justify-between">
              <Label className="text-sm cursor-pointer">Линии</Label>
              <Switch
                checked={mvtLayerSettings.showLines}
                onCheckedChange={(checked) =>
                  setMvtLayerSettings({ ...mvtLayerSettings, showLines: checked })
                }
              />
            </div>
            {mvtLayerSettings.showLines &&
              !mvtLayerSettings.symbolOnlyMode &&
              !mvtLayerSettings.iconsLabelsMode && (
              <div className="flex items-center gap-3 pl-1">
                <span className="text-xs text-gray-400 w-20 shrink-0">Прозрачность</span>
                <Slider
                  min={0}
                  max={100}
                  step={5}
                  value={[Math.round(mvtLayerSettings.lineOpacity * 100)]}
                  onValueChange={([v]) =>
                    setMvtLayerSettings({ ...mvtLayerSettings, lineOpacity: v / 100 })
                  }
                  className="flex-1"
                />
                <span className="text-xs text-gray-500 w-8 text-right">
                  {Math.round(mvtLayerSettings.lineOpacity * 100)}%
                </span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm cursor-pointer">Точки</Label>
              <Switch
                checked={mvtLayerSettings.showPoints}
                onCheckedChange={(checked) =>
                  setMvtLayerSettings({ ...mvtLayerSettings, showPoints: checked })
                }
                disabled={
                  mvtLayerSettings.symbolOnlyMode || Boolean(mvtLayerSettings.iconsLabelsMode)
                }
              />
            </div>
            {(mvtLayerSettings.showPoints || mvtLayerSettings.symbolOnlyMode) &&
              !mvtLayerSettings.iconsLabelsMode && (
              <div className="flex items-center gap-3 pl-1">
                <span className="text-xs text-gray-400 w-20 shrink-0">Прозрачность</span>
                <Slider
                  min={0}
                  max={100}
                  step={5}
                  value={[Math.round(mvtLayerSettings.pointOpacity * 100)]}
                  onValueChange={([v]) =>
                    setMvtLayerSettings({ ...mvtLayerSettings, pointOpacity: v / 100 })
                  }
                  className="flex-1"
                />
                <span className="text-xs text-gray-500 w-8 text-right">
                  {Math.round(mvtLayerSettings.pointOpacity * 100)}%
                </span>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}
